import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Wand2 } from "lucide-react";
import { CMSContent } from "@/types/cms";
import type { Json } from "@/integrations/supabase/types";

const defaultContent: CMSContent = {
  hero_title: "Welcome to Obotan",
  hero_description: "Your trusted platform for financial solutions",
  features_title: "Our Features",
  features: [],
};

const HomepageEditor = () => {
  const [content, setContent] = useState<CMSContent>(defaultContent);
  const { toast } = useToast();

  const { data: currentContent, isError } = useQuery({
    queryKey: ['homepage-content'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cms_content')
        .select('*')
        .eq('type', 'homepage')
        .maybeSingle();

      if (error) {
        console.error('Error fetching CMS content:', error);
        throw error;
      }

      return (data?.content as unknown as CMSContent) || defaultContent;
    }
  });

  useEffect(() => {
    if (currentContent) {
      setContent(currentContent);
    }
  }, [currentContent]);

  const handleAIAssist = async (field: string) => {
    toast({
      title: "AI Assistant",
      description: "Generating optimized content...",
    });
    // AI assistance implementation
  };

  const handleSave = async () => {
    try {
      const { error } = await supabase
        .from('cms_content')
        .upsert({
          type: 'homepage',
          content: content as unknown as Json
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Content updated successfully",
      });
    } catch (error) {
      console.error('Error saving CMS content:', error);
      toast({
        title: "Error",
        description: "Failed to update content",
        variant: "destructive",
      });
    }
  };

  if (isError) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500">Error loading CMS content. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Hero Section</h3>
            <Button variant="outline" onClick={() => handleAIAssist('hero')}>
              <Wand2 className="h-4 w-4 mr-2" />
              AI Assist
            </Button>
          </div>
          <div className="space-y-4">
            <div>
              <Input
                placeholder="Hero Title"
                value={content.hero_title}
                onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
              />
            </div>
            <div>
              <Textarea
                placeholder="Hero Description"
                value={content.hero_description}
                onChange={(e) => setContent({ ...content, hero_description: e.target.value })}
              />
            </div>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave}>Save Changes</Button>
      </div>
    </div>
  );
};

export default HomepageEditor;